import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useRef, useMemo, useEffect } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { typographyTemplate, bordersTemplate, useToken, mergeRefs, useHideBodyScroll, useGlobalKeyPress } from '@equinor/eds-utils';
import { dialog } from './Dialog.tokens.js';
import { jsx } from 'react/jsx-runtime';
import { Paper } from '../Paper/Paper.js';
import { useEds } from '../EdsProvider/eds.context.js';

var _excluded = ["children", "open", "onClose", "isDismissable", "dialogRef"];
var StyledDialog = styled(Paper).attrs({
  'aria-labelledby': 'eds-dialog-title',
  'aria-describedby': 'eds-dialog-customcontent',
  elevation: 'above_scrim'
}).withConfig({
  displayName: "Dialog__StyledDialog",
  componentId: "sc-1mewo3f-0"
})(function (_ref) {
  var theme = _ref.theme;
  return css(["width:", ";background:", ";display:grid;grid-auto-columns:auto;", ";", ";grid-gap:", ";"], theme.width, theme.background, typographyTemplate(theme.typography), bordersTemplate(theme.border), theme.spacings.bottom);
});
var StyledNativeDialog = styled.dialog.withConfig({
  displayName: "Dialog__StyledNativeDialog",
  componentId: "sc-1mewo3f-1"
})(function (_ref2) {
  var theme = _ref2.theme;
  return css(["padding:0;border:0;overflow:visible;overscroll-behavior:contain;", ";&::backdrop{background-color:", ";}"], bordersTemplate(theme.border), theme.entities.scrim.background);
});
var Dialog = /*#__PURE__*/forwardRef(function Dialog(_ref3, ref) {
  var children = _ref3.children,
    open = _ref3.open,
    onClose = _ref3.onClose,
    _ref3$isDismissable = _ref3.isDismissable,
    isDismissable = _ref3$isDismissable === void 0 ? false : _ref3$isDismissable,
    dialogRef = _ref3.dialogRef,
    props = _objectWithoutProperties(_ref3, _excluded);
  var _useEds = useEds(),
    density = _useEds.density;
  var localRef = useRef(null);
  var token = useToken({
    density: density
  }, dialog);
  var combinedDialogRef = useMemo(function () {
    return mergeRefs(localRef, dialogRef);
  }, [localRef, dialogRef]);
  useEffect(function () {
    if (open) {
      var _localRef$current;
      localRef === null || localRef === void 0 ? void 0 : (_localRef$current = localRef.current) === null || _localRef$current === void 0 ? void 0 : _localRef$current.showModal();
    } else {
      var _localRef$current2;
      localRef === null || localRef === void 0 ? void 0 : (_localRef$current2 = localRef.current) === null || _localRef$current2 === void 0 ? void 0 : _localRef$current2.close();
    }
  }, [open]);

  //This might become redundant in the future, see https://github.com/whatwg/html/issues/7732
  useHideBodyScroll(open);
  var handleDismiss = function handleDismiss(e) {
    var target = e.target;
    if (target instanceof HTMLElement) if (isDismissable && target.nodeName === 'DIALOG') {
      onClose && onClose();
    }
  };
  useGlobalKeyPress('Escape', function (e) {
    e.preventDefault();
    if (isDismissable && onClose && open) {
      onClose && onClose();
    }
  });
  return /*#__PURE__*/jsx(ThemeProvider, {
    theme: token,
    children: /*#__PURE__*/jsx(StyledNativeDialog, {
      ref: combinedDialogRef,
      onMouseDown: handleDismiss,
      children: open && /*#__PURE__*/jsx(StyledDialog, _objectSpread(_objectSpread({}, props), {}, {
        ref: ref,
        children: children
      }))
    })
  });
});

// Dialog.displayName = 'EdsDialog'

export { Dialog };
